import {extractAdjustmentRows} from '@/adjustment/adjust/utils/extractAdjustmentRows';
import {getAdjustmentTotal} from '@/adjustment/adjust/utils/adjustmentLockUtils';
import type {AdjustLineWithSignedAdjustment} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';

/** 与 WP 组装一致的 line 日志结构 */
export type AssemblyLineLog = AdjustLineWithSignedAdjustment & {
    statement?: 'BS' | 'PL' | string;
    scheduleName?: string;
    openingAmount?: number;
    closingAmount?: number;
};

export interface FromPaperAssemblyLogParams {
    source: 'setFromPaper' | 'setMultipleFromPaper' | 'loadFromHeader';
    code: string;
    companyId?: number;
    annualId?: number;
    lineMeta?: {
        name?: string;
        scheduleName?: string;
        fsId?: number;
    };
    adjustmentLine?: any;
    entry?: string | null;
    adjustment?: number;
    originalEntry?: string;
    rawAdjustment?: number;
    resolvedFields?: {
        entry: 'Dr' | 'Cr';
        adjustment: number;
        adjustmentAmount: number;
    };
}

export interface ToPaperAssemblyLogParams {
    source: 'loadFromHeader' | 'addToPaper' | 'setToPaper' | 'setUnifiedDetail';
    code: string;
    companyId?: number;
    annualId?: number;
    lineMeta?: {
        name?: string;
        scheduleName?: string;
        fsId?: number;
    };
    adjustmentLine?: any;
    entry?: string | null;
    adjustmentAmount?: number;
}

function normalizeRegistryKey(code: string): string {
    return String(code || '').trim().toUpperCase();
}

function unwrapDetails(details: any): any {
    if (!details) return null;
    return Array.isArray(details) ? details[0] : details;
}

function hasAdjustmentInDetails(code: string, details: any): boolean {
    const detailPayload = unwrapDetails(details);
    if (!detailPayload || typeof detailPayload !== 'object') return false;

    const rows = extractAdjustmentRows(code, detailPayload);
    return rows.some((row) => Math.abs(getAdjustmentTotal(row)) > 0.001);
}

function getStatementFromCode(code: string): 'BS' | 'PL' {
    return String(code || '').startsWith('PL-') ? 'PL' : 'BS';
}

function normalizeDetails(details: any): any[] {
    if (!details) return [];
    if (Array.isArray(details)) return details;
    return [details];
}

/**
 * 将 fromPaper 组装结果转换为 line 结构（与 unifiedMergeData / mergeDataForAdjust 一致）
 */
export function buildFromPaperAssemblyLine(params: FromPaperAssemblyLogParams): AssemblyLineLog {
    const {code, lineMeta, adjustmentLine, entry, adjustment, resolvedFields} = params;
    const registryKey = normalizeRegistryKey(code);

    return {
        fsId: lineMeta?.fsId ?? adjustmentLine?.fsId ?? 0,
        code: registryKey,
        currency: adjustmentLine?.currency ?? 'HKD',
        adjustment: resolvedFields?.adjustment ?? adjustment ?? adjustmentLine?.adjustment,
        adjustmentAmount: resolvedFields?.adjustmentAmount ?? adjustmentLine?.adjustmentAmount ?? adjustment,
        entry: resolvedFields?.entry ?? entry ?? adjustmentLine?.entry,
        statement: adjustmentLine?.statement ?? getStatementFromCode(registryKey),
        scheduleName: lineMeta?.scheduleName ?? lineMeta?.name ?? adjustmentLine?.scheduleName ?? registryKey,
        openingAmount: adjustmentLine?.openingAmount,
        closingAmount: adjustmentLine?.closingAmount,
        details: normalizeDetails(adjustmentLine?.details),
    };
}

/**
 * 将 toPaper 组装结果转换为 line 结构
 */
export function buildToPaperAssemblyLine(params: ToPaperAssemblyLogParams): AssemblyLineLog {
    const {code, lineMeta, adjustmentLine, entry, adjustmentAmount} = params;
    const registryKey = normalizeRegistryKey(code);

    return {
        fsId: lineMeta?.fsId ?? adjustmentLine?.fsId ?? 0,
        code: registryKey,
        currency: adjustmentLine?.currency ?? 'HKD',
        adjustmentAmount: adjustmentAmount ?? adjustmentLine?.adjustmentAmount ?? 0,
        adjustment: adjustmentLine?.adjustment ?? adjustmentAmount ?? adjustmentLine?.adjustmentAmount ?? 0,
        entry: entry ?? adjustmentLine?.entry ?? null,
        statement: adjustmentLine?.statement ?? getStatementFromCode(registryKey),
        scheduleName: lineMeta?.scheduleName ?? lineMeta?.name ?? adjustmentLine?.scheduleName ?? registryKey,
        details: normalizeDetails(adjustmentLine?.details),
    };
}

function computeDetailAdjustmentTotal(code: string, detail: any): number {
    if (!detail) return 0;
    const registryKey = normalizeRegistryKey(code);
    const rows = extractAdjustmentRows(registryKey, detail);
    return rows.reduce((sum, row) => sum + (getAdjustmentTotal(row) || 0), 0);
}

function buildToPaperAdjustmentLine(params: ToPaperAssemblyLogParams, detail?: any): any {
    const registryKey = normalizeRegistryKey(params.code);
    const resolvedDetail = detail ?? params.adjustmentLine?.details;
    const detailPayload = unwrapDetails(resolvedDetail) ?? detail;
    const computedAdjustment = computeDetailAdjustmentTotal(registryKey, detailPayload);

    return {
        companyId: params.companyId,
        annualId: params.annualId,
        scheduleCode: registryKey,
        fsId: params.lineMeta?.fsId ?? params.adjustmentLine?.fsId ?? 0,
        currency: params.adjustmentLine?.currency ?? 'HKD',
        entry: params.entry ?? params.adjustmentLine?.entry,
        adjustmentAmount: params.adjustmentAmount ?? params.adjustmentLine?.adjustmentAmount ?? Math.abs(computedAdjustment),
        adjustment: params.adjustmentLine?.adjustment ?? computedAdjustment,
        details: detailPayload ? [detailPayload] : normalizeDetails(params.adjustmentLine?.details),
        statement: params.adjustmentLine?.statement ?? getStatementFromCode(registryKey),
    };
}

function shouldLogFromPaperAssembly(code: string, line: AssemblyLineLog, adjustmentLine?: any): boolean {
    if (process.env.NODE_ENV === 'production') return false;

    const registryKey = normalizeRegistryKey(code);
    const hasLineAdjustment =
        Math.abs(Number(line.adjustment) || 0) > 0.001 ||
        Math.abs(Number(line.adjustmentAmount) || 0) > 0.001;
    const hasDetailsAdjustment = hasAdjustmentInDetails(registryKey, adjustmentLine?.details ?? line.details);

    return hasLineAdjustment || hasDetailsAdjustment;
}

function shouldLogToPaperAssemblyEnv(): boolean {
    return process.env.NODE_ENV !== 'production';
}

function shouldLogToPaperAssembly(
    code: string,
    line: AssemblyLineLog,
    adjustmentLine?: any,
    sourceDetails?: any,
): boolean {
    if (!shouldLogToPaperAssemblyEnv()) return false;

    const registryKey = normalizeRegistryKey(code);
    const hasLineAdjustment =
        Math.abs(Number(line.adjustmentAmount) || 0) > 0.001 ||
        Math.abs(Number(line.adjustment) || 0) > 0.001;
    const resolvedDetails = sourceDetails ?? adjustmentLine?.details ?? line.details;
    const hasDetailsAdjustment = hasAdjustmentInDetails(registryKey, resolvedDetails);

    return hasLineAdjustment || hasDetailsAdjustment;
}

/**
 * fromPaper 数据组装完成后，以 line 结构输出日志（仅开发环境）。
 */
export function logFromPaperAssembly(params: FromPaperAssemblyLogParams): void {
    const line = buildFromPaperAssemblyLine(params);
    if (!shouldLogFromPaperAssembly(params.code, line, params.adjustmentLine)) return;

    const registryKey = normalizeRegistryKey(params.code);
    const detailPayload = unwrapDetails(params.adjustmentLine?.details ?? line.details);
    const adjustmentRowCount = detailPayload
        ? extractAdjustmentRows(registryKey, detailPayload).length
        : 0;
    //
    // console.log('[adjustDraftStore] fromPaper assembly (line)', {
    //     source: params.source,
    //     componentType: getComponentType(registryKey),
    //     companyId: params.companyId,
    //     annualId: params.annualId,
    //     originalEntry: params.originalEntry,
    //     rawAdjustment: params.rawAdjustment,
    //     adjustmentRowCount,
    //     line,
    // });
}

/**
 * toPaper 数据组装完成后，以 line 结构输出日志（仅开发环境）。
 * 仅当组装后 adjustmentAmount / adjustment 非 0，或 details 内含调整行时输出。
 */
export function logToPaperAssembly(params: ToPaperAssemblyLogParams, detail?: any): void {
    const registryKey = normalizeRegistryKey(params.code);
    const sourceDetails = detail ?? params.adjustmentLine?.details;
    const adjustmentLine = buildToPaperAdjustmentLine(params, detail);
    const resolvedEntry = params.entry ?? adjustmentLine.entry ?? null;
    const line = buildToPaperAssemblyLine({
        ...params,
        entry: resolvedEntry,
        adjustmentLine,
        adjustmentAmount: adjustmentLine.adjustmentAmount,
    });

    if (!shouldLogToPaperAssembly(registryKey, line, adjustmentLine, sourceDetails)) return;

    const detailPayload = unwrapDetails(sourceDetails ?? adjustmentLine.details ?? line.details);
    const adjustmentRowCount = detailPayload
        ? extractAdjustmentRows(registryKey, detailPayload).length
        : 0;
    const details = normalizeDetails(sourceDetails ?? adjustmentLine.details ?? line.details);

    // console.log('[adjustDraftStore] toPaper assembly (line)', {
    //     source: params.source,
    //     componentType: getComponentType(registryKey),
    //     companyId: params.companyId,
    //     annualId: params.annualId,
    //     entry: resolvedEntry,
    //     details,
    //     adjustmentRowCount,
    //     assembledAdjustmentAmount: line.adjustmentAmount,
    //     assembledAdjustment: line.adjustment,
    //     line: {
    //         ...line,
    //         entry: resolvedEntry,
    //         details,
    //     },
    // });
}

/**
 * loadFromHeader 中批量 toPaper 组装完成后输出日志
 */
export function logToPaperAssemblyBatch(
    source: ToPaperAssemblyLogParams['source'],
    companyId: number | undefined,
    annualId: number | undefined,
    items: Array<Omit<ToPaperAssemblyLogParams, 'source' | 'companyId' | 'annualId'>>,
    detailsByCode?: Record<string, any>,
): void {
    items.forEach((item) => {
        const code = normalizeRegistryKey(item.code);
        logToPaperAssembly({
            source,
            companyId,
            annualId,
            ...item,
        }, detailsByCode?.[code] ?? detailsByCode?.[item.code]);
    });
}
